import {
  Badge,
  Button,
  DataTable,
  FilterToolbar,
  PageHeader,
  SearchInput,
  Select,
  statusBadge,
  Truncate,
  useDebouncedText,
  useUrlFilters,
  type Column,
  type FilterItem,
  type SortState,
} from "@flexfreight/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { AddCustomerDialog } from "./Onboarding";

export type CustomerRow = {
  id: string;
  name: string;
  prospect: boolean;
  createdAt: string;
  creditTermsDays: number | null;
  users: number;
  spend90: { cents: number; currency: "USD" };
  shipments90: number;
  lastActivity: string | null;
};

export const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const dayFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
export const shortDate = (iso: string) => dayFmt.format(new Date(iso));
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/** URL filter defaults. The API takes every key (filterQuery), the URL keeps only what differs. */
const FILTERS = { q: "", kind: "all", activity: "all", terms: "all", sort: "recent", dir: "desc", page: 0 };
const PAGE_SIZE = 50;

/** Table column key -> API sort key. */
const SORT_KEY: Record<string, string> = { name: "name", spend: "spend", shipments: "shipments", activity: "activity", since: "recent" };
const COLUMN_FOR_SORT = Object.fromEntries(Object.entries(SORT_KEY).map(([col, s]) => [s, col]));

/** Built-in views: named filter presets (the view reads "Custom" when filters are changed by hand). */
const VIEWS = [
  { id: "all", label: "All accounts", set: { kind: "all", activity: "all", terms: "all" } },
  { id: "active", label: "Shipping now", set: { kind: "customers", activity: "active", terms: "all" } },
  { id: "dormant", label: "No shipments in 90 days", set: { kind: "customers", activity: "dormant", terms: "all" } },
  { id: "prospects", label: "Prospects", set: { kind: "prospects", activity: "all", terms: "all" } },
] as const;

/** Customers > All customers: every account with users, 90-day spend and last activity. Filters live in the URL. */
export function CustomersList() {
  const f = useUrlFilters(FILTERS, useSearchParams());
  const v = f.values;
  const [search, setSearch] = useDebouncedText(v.q, (q) => f.set({ q }));
  const me = useConsoleMe();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);

  const list = useQuery({
    queryKey: ["console", "customers", f.query.toString()],
    queryFn: () => api<{ customers: CustomerRow[]; hasMore: boolean }>(`/v1/console/customers?${f.query}`),
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.customers ?? [];
  const sort: SortState = { key: COLUMN_FOR_SORT[v.sort] ?? "since", dir: v.dir === "asc" ? "asc" : "desc" };
  const view = VIEWS.find((x) => x.set.kind === v.kind && x.set.activity === v.activity && x.set.terms === v.terms)?.id ?? null;

  const items: FilterItem[] = [
    {
      key: "kind",
      priority: 1,
      active: v.kind !== "all",
      inline: <Select variant="toolbar" label="Type" value={v.kind} onChange={(kind) => f.set({ kind })} options={[{ value: "all", label: "All" }, { value: "customers", label: "Customers" }, { value: "prospects", label: "Prospects" }]} />,
    },
    {
      key: "activity",
      priority: 2,
      active: v.activity !== "all",
      inline: <Select variant="toolbar" label="Activity" value={v.activity} onChange={(activity) => f.set({ activity })} options={[{ value: "all", label: "Any" }, { value: "active", label: "Shipped in 90 days" }, { value: "dormant", label: "No shipments in 90 days" }]} />,
    },
    {
      key: "terms",
      priority: 3,
      active: v.terms !== "all",
      inline: <Select variant="toolbar" label="Terms" value={v.terms} onChange={(terms) => f.set({ terms })} options={[{ value: "all", label: "Any" }, { value: "prepay", label: "Prepay" }, { value: "credit", label: "Credit terms" }]} />,
    },
  ];

  const prospect = statusBadge("customerKind", "prospect");
  const columns: Column<CustomerRow>[] = [
    {
      key: "name",
      header: "Customer",
      primary: true,
      sortable: true,
      truncate: "custom",
      render: (r) => (
        <span className="ffx-cellrow">
          <Truncate text={r.name}>
            <Link className="ffa-rowlink" to={`/customers/${r.id}`}>
              {r.name}
            </Link>
          </Truncate>
          {r.prospect ? (
            <Badge tone={prospect.tone} size="sm" dot={false}>
              {prospect.label}
            </Badge>
          ) : null}
        </span>
      ),
    },
    { key: "users", header: "Users", numeric: true, width: "6rem", render: (r) => r.users },
    { key: "shipments", header: "Shipments (90d)", numeric: true, sortable: true, width: "10rem", render: (r) => r.shipments90 },
    { key: "spend", header: "Spend (90d)", numeric: true, sortable: true, width: "9.5rem", render: (r) => usd.format(r.spend90.cents / 100) },
    {
      key: "activity",
      header: "Last activity",
      sortable: true,
      sortFirst: "desc",
      width: "9.5rem",
      render: (r) => (r.lastActivity ? <time dateTime={r.lastActivity}>{shortDate(r.lastActivity)}</time> : <span className="ffa-muted">None</span>),
    },
    { key: "terms", header: "Terms", width: "7rem", mobile: "hide", render: (r) => (r.creditTermsDays ? `Net ${r.creditTermsDays}` : <span className="ffa-muted">Prepay</span>) },
    { key: "since", header: "Since", sortable: true, sortFirst: "desc", width: "8.5rem", mobile: "hide", render: (r) => <time dateTime={r.createdAt}>{shortDate(r.createdAt)}</time> },
  ];

  return (
    <section className="ffa-page">
      <PageHeader
        title="Customers"
        subtitle="Every customer account and prospect, with users, spend and recent activity."
        primaryAction={me.capabilities.includes("customers:write") ? <Button variant="primary" onClick={() => setAdding(true)}>Add customer</Button> : undefined}
      />
      {adding ? <AddCustomerDialog onClose={() => setAdding(false)} /> : null}
      <FilterToolbar
        label="Customer filters"
        search={<SearchInput label="Search customers" value={search} onChange={setSearch} />}
        items={items}
        anyActive={f.anyActive}
        showReset
        onReset={() => {
          setSearch("");
          f.reset();
        }}
        views={{
          items: VIEWS.map((x) => ({ id: x.id, label: x.label })),
          value: view,
          onChange: (id) => {
            const next = VIEWS.find((x) => x.id === id);
            if (next) f.set({ ...next.set });
          },
        }}
      />
      <DataTable
        caption="Customers"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={list.isPending || list.isFetching}
        error={list.isError ? { title: "Couldn't load customers", message: message(list.error), onRetry: () => void list.refetch() } : null}
        empty={f.anyActive ? "No customers match these filters." : "No customers yet."}
        sort={sort}
        onSortChange={(s) => f.set({ sort: SORT_KEY[s?.key ?? "since"] ?? "recent", dir: s?.dir ?? "desc" })}
        onRowClick={(r) => navigate(`/customers/${r.id}`)}
        rowHref={(r) => `/customers/${r.id}`}
        columnsMenu
        pagination={{ page: v.page, pageSize: PAGE_SIZE, hasMore: !!list.data?.hasMore, onPageChange: (page) => f.set({ page }) }}
      />
    </section>
  );
}
